import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Header } from './components/Layout/Header';
import { Footer } from './components/Layout/Footer';
import { ScrollToTop } from './components/Common/ScrollToTop';
import { HomePage } from './pages/HomePage';
import { CategoryPage } from './pages/CategoryPage';
import { ToolPage } from './pages/ToolPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { TermsPage } from './pages/TermsPage';
import { AboutPage } from './pages/AboutPage';
import { FAQPage } from './pages/FAQPage';
import { HowToUsePage } from './pages/HowToUsePage';
import { ContactPage } from './pages/ContactPage';
import { getAllTools } from './data/tools';
import { toolSEOData } from './data/toolSEO';
import { initGA, trackPageView, trackCategoryView } from './services/analytics';
import { GA_MEASUREMENT_ID, shouldTrackAnalytics } from './config/analytics';



// Route-based SEO component
const RouteSEO: React.FC = () => {
  const location = useLocation();
  const tools = getAllTools();
  
  useEffect(() => {
    const path = location.pathname;
    
    if (path === '/') {
      // Homepage SEO
      const title = "Private Tools - Free Privacy-First Online Tools";
      const description = "Free privacy-first online tools for images, PDFs, text, and encryption. Process files locally in your browser - no uploads, no storage.";
      const url = "https://privatetools.tech/";
      
      // Track page view
      if (shouldTrackAnalytics) {
        trackPageView(title, path);
      }
      
      document.title = title;
      updateMetaDescription(description);
      updateMetaKeywords("online tools, privacy tools, image converter, PDF tools, text tools, encryption tools, developer tools, client-side processing, no upload tools, free online tools, image compression, PDF merger, text analyzer, hash generator, base64 encoder, QR code generator");
      updateCanonical(url);
      updateOpenGraph(title, description, url);
      updateTwitterCard(title, description, url);
          } else if (path.startsWith('/category/')) {
        // Category page SEO
        const categoryId = path.split('/')[2];
      const categoryTools = tools.filter(t => t.category === categoryId);
      const toolNames = categoryTools.map(t => t.name).join(', ');
      const title = `${categoryId.charAt(0).toUpperCase() + categoryId.slice(1)} Tools - Free Online ${categoryId} Processing Tools | PrivateTools`;
      const description = `Free online ${categoryId} tools. Process ${categoryId} files locally in your browser.${toolNames ? ' ' + toolNames + '.' : ''} No uploads, no registration, complete privacy.`;
      const url = `https://privatetools.tech${path}`;
      
      // Track category view
      if (shouldTrackAnalytics) {
        trackPageView(title, path);
        trackCategoryView(categoryId);
      }
      
      document.title = title;
      updateMetaDescription(description);
      updateMetaKeywords(`${categoryId} tools, online ${categoryId} converter, free ${categoryId} tools, ${categoryId} processing${toolNames ? ', ' + toolNames : ''}`);
      updateCanonical(url);
      updateOpenGraph(title, description, url);
      updateTwitterCard(title, description, url);
          } else if (path.startsWith('/tools/')) {
        // Individual tool page SEO - Using personalized SEO data
        const toolId = path.split('/')[2];
        const tool = tools.find(t => t.id === toolId);
        const toolSEO = toolSEOData[toolId];
        
        if (tool && toolSEO) {
          const title = toolSEO.title;
          const description = toolSEO.description;
          const keywords = toolSEO.keywords;
          const url = `https://privatetools.tech${path}`;
          
          // Track page view
          if (shouldTrackAnalytics) {
            trackPageView(title, path);
          }
          
          // Generate structured data for the tool
          const structuredData = {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": tool.name,
            "description": toolSEO.description,
            "url": url,
            "applicationCategory": "UtilitiesApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "creator": {
              "@type": "Organization",
              "name": "PrivateTools"
            },
            "featureList": toolSEO.searchTerms,
            "keywords": toolSEO.keywords
          };
        
        document.title = title;
        updateMetaDescription(description);
        updateMetaKeywords(keywords);
        updateCanonical(url);
        updateOpenGraph(title, description, url);
        updateTwitterCard(title, description, url);
        updateStructuredData(structuredData);
        }
          } else if (path === '/privacy') {
        const title = "Privacy Policy - PrivateTools | 100% Privacy-First Online Tools";
        const description = "Our privacy policy explains how PrivateTools protects your data. We don't collect, store, or process any personal information. Complete privacy guaranteed.";
        const url = "https://privatetools.tech/privacy";
        
        // Track page view
        if (shouldTrackAnalytics) {
          trackPageView(title, path);
        }
      
      document.title = title;
      updateMetaDescription(description);
      updateMetaKeywords("privacy policy, data protection, no tracking, client-side processing, privacy-first tools");
      updateCanonical(url);
      updateOpenGraph(title, description, url);
      updateTwitterCard(title, description, url);
      } else if (path === '/terms') {
        const title = "Terms of Use - PrivateTools | Free Online Tools";
        const description = "Terms of use for PrivateTools. Learn about our service terms, user responsibilities, and how we provide free, privacy-first online tools.";
        const url = "https://privatetools.tech/terms";
        
        // Track page view
        if (shouldTrackAnalytics) {
          trackPageView(title, path);
        }
      
      document.title = title;
      updateMetaDescription(description);
      updateMetaKeywords("terms of use, service terms, user agreement, free tools terms");
      updateCanonical(url);
      updateOpenGraph(title, description, url);
      updateTwitterCard(title, description, url);
      } else if (path === '/about') {
        const title = "About PrivateTools | Privacy-First Online Tools Platform";
        const description = "Learn about PrivateTools - a platform providing 40+ privacy-first online tools. All processing happens locally in your browser. No uploads, no storage.";
        const url = "https://privatetools.tech/about";
        
        // Track page view
        if (shouldTrackAnalytics) {
          trackPageView(title, path);
        }
      
      document.title = title;
      updateMetaDescription(description);
      updateMetaKeywords("about PrivateTools, privacy-first tools, client-side processing, online tools platform");
      updateCanonical(url);
      updateOpenGraph(title, description, url);
      updateTwitterCard(title, description, url);
      } else if (path === '/faq') {
        const title = "FAQ - Frequently Asked Questions | PrivateTools";
        const description = "Find answers to common questions about PrivateTools, our privacy practices, and how to use our tools effectively. Get help with any issues.";
        const url = "https://privatetools.tech/faq";
        
        // Track page view
        if (shouldTrackAnalytics) {
          trackPageView(title, path);
        }
      
      document.title = title;
      updateMetaDescription(description);
      updateMetaKeywords("FAQ, frequently asked questions, help, support, privacy tools, how to use");
      updateCanonical(url);
      updateOpenGraph(title, description, url);
      updateTwitterCard(title, description, url);
      } else if (path === '/how-to-use') {
        const title = "How to Use Guide - Step-by-Step Instructions | PrivateTools";
        const description = "Complete guide on how to use all PrivateTools. Step-by-step instructions, tips, and best practices for every tool category.";
        const url = "https://privatetools.tech/how-to-use";
        
        // Track page view
        if (shouldTrackAnalytics) {
          trackPageView(title, path);
        }
      
      document.title = title;
      updateMetaDescription(description);
      updateMetaKeywords("how to use, guide, instructions, tutorial, help, tools guide, step by step");
      updateCanonical(url);
      updateOpenGraph(title, description, url);
      updateTwitterCard(title, description, url);
      } else if (path === '/contact') {
        const title = "Contact Us - Get Help & Support | PrivateTools";
        const description = "Contact PrivateTools for support, bug reports, feature requests, or general inquiries. We're here to help with any questions.";
        const url = "https://privatetools.tech/contact";
        
        // Track page view
        if (shouldTrackAnalytics) {
          trackPageView(title, path);
        }
      
      document.title = title;
      updateMetaDescription(description);
      updateMetaKeywords("contact, support, help, bug report, feature request, feedback, customer service");
      updateCanonical(url);
      updateOpenGraph(title, description, url);
      updateTwitterCard(title, description, url);
      } else {
        // Default SEO
        const title = "Private Tools - Free Privacy-First Online Tools";
        const description = "Free privacy-first online tools for images, PDFs, text, and encryption. Process files locally in your browser - no uploads, no storage.";
      const url = "https://privatetools.tech/";
        
      document.title = title;
      updateMetaDescription(description);
      updateMetaKeywords("online tools, privacy tools, image converter, PDF tools, text tools, encryption tools, developer tools, client-side processing, no upload tools");
      updateCanonical(url);
      updateOpenGraph(title, description, url);
      updateTwitterCard(title, description, url);
      }
  }, [location.pathname, tools]);

  return null;
};

// Helper functions for updating meta tags
const updateMetaDescription = (description: string) => {
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }
  metaDescription.setAttribute('content', description);
};

const updateMetaKeywords = (keywords: string) => {
  let metaKeywords = document.querySelector('meta[name="keywords"]');
  if (!metaKeywords) {
    metaKeywords = document.createElement('meta');
    metaKeywords.setAttribute('name', 'keywords');
    document.head.appendChild(metaKeywords);
  }
  metaKeywords.setAttribute('content', keywords);
};

const updateCanonical = (url: string) => {
  let canonicalLink = document.querySelector('link[rel="canonical"]');
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalLink);
  }
  canonicalLink.setAttribute('href', url);
};

const updateOpenGraph = (title: string, description: string, url: string) => {
  const updateOGTag = (property: string, content: string) => {
    let ogTag = document.querySelector(`meta[property="${property}"]`);
    if (!ogTag) {
      ogTag = document.createElement('meta');
      ogTag.setAttribute('property', property);
      document.head.appendChild(ogTag);
    }
    ogTag.setAttribute('content', content);
  };
  
  updateOGTag('og:title', title);
  updateOGTag('og:description', description);
  updateOGTag('og:url', url);
  updateOGTag('og:image', 'https://privatetools.tech/og-image.svg');
  updateOGTag('og:type', 'website');
  updateOGTag('og:site_name', 'Private Tools');
};

const updateTwitterCard = (title: string, description: string, url: string) => {
  const updateTwitterTag = (name: string, content: string) => {
    let twitterTag = document.querySelector(`meta[name="twitter:${name}"]`);
    if (!twitterTag) {
      twitterTag = document.createElement('meta');
      twitterTag.setAttribute('name', `twitter:${name}`);
      document.head.appendChild(twitterTag);
    }
    twitterTag.setAttribute('content', content);
  };
  
  updateTwitterTag('card', 'summary_large_image');
  updateTwitterTag('title', title);
  updateTwitterTag('description', description);
  updateTwitterTag('image', 'https://privatetools.tech/og-image.svg');
  updateTwitterTag('url', url);
};

const updateStructuredData = (data: Record<string, unknown>) => {
  // Remove existing structured data
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }
  
  // Add new structured data
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

// Smooth scrolling utility
const smoothScrollTo = (element: HTMLElement | null, offset: number = 0) => {
  if (!element) return;
  
  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - offset;

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth'
  });
};

function App() {
  const [darkMode, setDarkMode] = useState(false);

  // Initialize Google Analytics
  useEffect(() => {
    if (shouldTrackAnalytics) {
      initGA(GA_MEASUREMENT_ID);
    }
  }, []);



  useEffect(() => {
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode) {
      setDarkMode(JSON.parse(savedMode));
    }
  }, []);

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', JSON.stringify(newMode));
  };

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Add smooth scrolling to window object for global access
  useEffect(() => {
    (window as Window & { smoothScrollTo?: typeof smoothScrollTo }).smoothScrollTo = smoothScrollTo;
  }, []);

  return (
    <Router>
      <div className={`min-h-screen bg-white dark:bg-gray-900 transition-colors ${darkMode ? 'dark' : ''}`}>
        <RouteSEO />
        <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/category/:categoryId" element={<CategoryPage />} />
            <Route path="/tools/:toolId" element={<ToolPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/how-to-use" element={<HowToUsePage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </Router>
  );
}

export default App;